import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchDeleghe, updateDelega, fetchUsers, fetchOrdini } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Users, Edit, CheckCircle, AlertTriangle, Search, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function DeleghePage() {
  const { user, isAmministratore, isAdminOrdine, isConsigliere } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check for simplified view (Admin Ordine)
  const isSimplifiedView = searchParams.get('simplified') === 'true';
  const filterOrdineId = searchParams.get('ordine');
  
  // Consigliere C3i has read-only access
  const isReadOnly = isConsigliere();
  
  const [deleghe, setDeleghe] = useState([]);
  const [users, setUsers] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [selectedDelega, setSelectedDelega] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Filters and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegione, setSelectedRegione] = useState('all');
  const [selectedStato, setSelectedStato] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('ordine');
  const [sortDirection, setSortDirection] = useState('asc');
  const itemsPerPage = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [delegheData, usersData, ordiniData] = await Promise.all([
        fetchDeleghe({ limit: 200 }),
        fetchUsers(),
        fetchOrdini({ limit: 200 })
      ]);
      
      setDeleghe(delegheData.items || delegheData);
      setUsers(usersData);
      setOrdini(ordiniData.items || ordiniData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveDelega = async () => {
    setSaving(true);
    try {
      await updateDelega(selectedDelega.id, {
        delegato_id: selectedDelega.delegato_id,
        supplente_id: selectedDelega.supplente_id
      });
      toast.success('Delega aggiornata con successo');
      setEditModal(false);
      loadData();
      
      // If simplified view, go back to dashboard after save
      if (isSimplifiedView) {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento della delega');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Caricamento...</div>
      </div>
    );
  }

  // Filter ordini: exclude "Nessun Ordine" from deleghe list
  const filteredOrdini = ordini.filter(o => o.id !== 'nessun-ordine');

  // Count total delegati
  const totalDelegati = users.filter(u => 
    u.ruoli_senza_accesso?.includes('DELEGATO_C3I')
  ).length;

  // Get unique regions sorted alphabetically (exclude "Nessun Ordine")
  const regioni = [...new Set(filteredOrdini.map(o => o.regione))].filter(r => r && r !== '-').sort((a, b) => 
    a.localeCompare(b, 'it', { sensitivity: 'base' })
  );

  // Filter and sort deleghe
  let filteredDeleghe = deleghe
    .filter(delega => delega.ordine_id !== 'nessun-ordine')
    .map(delega => {
    const ordine = filteredOrdini.find(o => o.id === delega.ordine_id);
    const delegato = users.find(u => u.id === delega.delegato_id);
    const supplente = users.find(u => u.id === delega.supplente_id);
    
    return {
      ...delega,
      ordine,
      delegato,
      supplente,
      stato: (delegato || supplente) ? 'Presente' : 'Assente'
    };
  });

  // Search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredDeleghe = filteredDeleghe.filter(d => {
      const ordineText = d.ordine?.denominazione?.toLowerCase() || '';
      const delegatoText = d.delegato ? `${d.delegato.nome} ${d.delegato.cognome}`.toLowerCase() : '';
      return ordineText.includes(query) || delegatoText.includes(query);
    });
  }

  // Region filter
  if (selectedRegione !== 'all') {
    filteredDeleghe = filteredDeleghe.filter(d => d.ordine?.regione === selectedRegione);
  }

  // State filter
  if (selectedStato !== 'all') {
    filteredDeleghe = filteredDeleghe.filter(d => d.stato === selectedStato);
  }

  // Sorting
  filteredDeleghe.sort((a, b) => {
    let aValue, bValue;
    
    switch(sortField) {
      case 'ordine':
        aValue = a.ordine?.sigla_provincia || '';
        bValue = b.ordine?.sigla_provincia || '';
        break;
      case 'regione':
        aValue = a.ordine?.regione || '';
        bValue = b.ordine?.regione || '';
        break;
      case 'stato':
        aValue = a.stato;
        bValue = b.stato;
        break;
      default:
        aValue = a.ordine?.sigla_provincia || '';
        bValue = b.ordine?.sigla_provincia || '';
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination
  const totalItems = filteredDeleghe.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDeleghe = filteredDeleghe.slice(startIndex, startIndex + itemsPerPage);

  // Get users from the specific ordine for simplified view
  const ordineUsers = filterOrdineId 
    ? users.filter(u => u.ordine_appartenenza_id === filterOrdineId)
    : [];

  // Simplified View for Admin Ordine
  if (isSimplifiedView && filterOrdineId) {
    const ordineDelega = filteredDeleghe.find(d => d.ordine?.id === filterOrdineId);
    const ordine = filteredOrdini.find(o => o.id === filterOrdineId);
    
    if (!ordineDelega || !ordine) {
      return (
        <div className="container-institutional">
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-500">Delega non trovata</div>
          </div>
        </div>
      );
    }

    return (
      <div className="container-institutional" data-testid="deleghe-simplified">
        {/* Header with back button */}
        <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 pl-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Dashboard
            </Button>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-4 pl-4">Gestione Deleghe</h1>
          <p className="text-slate-500 mt-1 pl-4">{ordine.denominazione}</p>
        </div>

        <Card className="card-institutional">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Delega del tuo Ordine</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="table-responsive">
              <table className="data-table" data-testid="deleghe-simplified-table">
                <thead>
                  <tr>
                    <th>Delegato</th>
                    <th>Supplente</th>
                    <th>Stato</th>
                    <th className="w-24">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {ordineDelega.delegato ? (
                        <div>
                          <p className="font-medium text-slate-900">
                            {ordineDelega.delegato.nome} {ordineDelega.delegato.cognome}
                          </p>
                          <p className="text-xs text-slate-500">{ordineDelega.delegato.email}</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">Non nominato</span>
                      )}
                    </td>
                    <td>
                      {ordineDelega.supplente ? (
                        <div>
                          <p className="font-medium text-slate-900">
                            {ordineDelega.supplente.nome} {ordineDelega.supplente.cognome}
                          </p>
                          <p className="text-xs text-slate-500">{ordineDelega.supplente.email}</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">Non nominato</span>
                      )}
                    </td>
                    <td>
                      {ordineDelega.stato === 'Presente' ? (
                        <span className="badge-success flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Presente
                        </span>
                      ) : (
                        <span className="badge-warning flex items-center gap-1 w-fit">
                          <AlertTriangle className="w-3 h-3" />
                          Assente
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedDelega({
                            ...ordineDelega,
                            delegato_id: ordineDelega.delegato?.id || '',
                            supplente_id: ordineDelega.supplente?.id || ''
                          });
                          setEditModal(true);
                        }}
                        data-testid="edit-delega-btn"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Modal for Simplified View */}
        <Dialog open={editModal} onOpenChange={setEditModal}>
          <DialogContent className="max-w-lg" data-testid="edit-delega-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifica Delega</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Delegato C3i</Label>
                <Select 
                  value={selectedDelega?.delegato_id || 'none'} 
                  onValueChange={(v) => setSelectedDelega({ 
                    ...selectedDelega, 
                    delegato_id: v === 'none' ? null : v 
                  })}
                >
                  <SelectTrigger data-testid="delegato-select">
                    <SelectValue placeholder="Seleziona delegato..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun delegato</SelectItem>
                    {ordineUsers
                      .filter(u => u.id !== selectedDelega?.supplente_id)
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.nome} {u.cognome}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Delegato Supplente</Label>
                <Select 
                  value={selectedDelega?.supplente_id || 'none'} 
                  onValueChange={(v) => setSelectedDelega({ 
                    ...selectedDelega, 
                    supplente_id: v === 'none' ? null : v 
                  })}
                >
                  <SelectTrigger data-testid="supplente-select">
                    <SelectValue placeholder="Seleziona supplente..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun supplente</SelectItem>
                    {ordineUsers
                      .filter(u => u.id !== selectedDelega?.delegato_id)
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.nome} {u.cognome}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal(false)}>Annulla</Button>
              <Button onClick={handleSaveDelega} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="container-institutional">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900 pl-4">Delegati C3i</h1>
        <p className="text-slate-500 mt-1 pl-4">{totalDelegati} delegati</p>
      </div>

      <Card className="card-institutional">
        <CardHeader>
          {!isReadOnly && (
            <CardTitle className="font-heading text-lg">Gestione Deleghe</CardTitle>
          )}
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Cerca per ordine o delegato"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedRegione} onValueChange={(v) => { setSelectedRegione(v); setCurrentPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Tutte le Regioni" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le Regioni</SelectItem>
                {regioni.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedStato} onValueChange={(v) => { setSelectedStato(v); setCurrentPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Tutti gli Stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli Stati</SelectItem>
                <SelectItem value="Assente">Assente</SelectItem>
                <SelectItem value="Presente">Presente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ordine</th>
                  {isReadOnly ? (
                    <>
                      <th>Stato</th>
                      <th>Delegato</th>
                      <th>Email</th>
                      <th>Cellulare</th>
                    </>
                  ) : (
                    <>
                      <th>Regione</th>
                      <th>Delegato</th>
                      <th>Supplente</th>
                      <th>Stato</th>
                      <th className="w-24">Azioni</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedDeleghe.map((delega) => (
                  <tr key={delega.id}>
                    <td className="font-medium">{delega.ordine?.denominazione || '-'}</td>
                    {isReadOnly ? (
                      <>
                        <td>
                          {delega.stato === 'Presente' ? (
                            <span className="badge-success flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              Presente
                            </span>
                          ) : (
                            <span className="badge-warning flex items-center gap-1 w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              Assente
                            </span>
                          )}
                        </td>
                        <td>
                          {delega.delegato ? (
                            <span>{delega.delegato.nome} {delega.delegato.cognome}</span>
                          ) : (
                            <span className="text-slate-400 italic">-</span>
                          )}
                        </td>
                        <td className="text-sm">
                          {delega.delegato?.email ? (
                            <a href={`mailto:${delega.delegato.email}`} className="text-blue-600 hover:underline">
                              {delega.delegato.email}
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="text-sm">
                          {delega.delegato?.cellulare ? (
                            <a href={`tel:${delega.delegato.cellulare}`} className="hover:text-slate-900">
                              {delega.delegato.cellulare}
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="text-sm">{delega.ordine?.regione || '-'}</td>
                        <td>
                          {delega.delegato ? (
                            <span>{delega.delegato.nome} {delega.delegato.cognome}</span>
                          ) : (
                            <span className="text-slate-400 italic">Non nominato</span>
                          )}
                        </td>
                        <td>
                          {delega.supplente ? (
                            <span>{delega.supplente.nome} {delega.supplente.cognome}</span>
                          ) : (
                            <span className="text-slate-400 italic">Non nominato</span>
                          )}
                        </td>
                        <td>
                          {delega.stato === 'Presente' ? (
                            <span className="badge-success flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              Presente
                            </span>
                          ) : (
                            <span className="badge-warning flex items-center gap-1 w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              Assente
                            </span>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedDelega(delega);
                              setEditModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col items-center border-t pt-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Precedente
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="next-page-btn"
                >
                  Successiva
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="text-sm text-slate-500 mt-2">
                Pagina {currentPage} di {totalPages} ({totalItems} deleghe)
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Delega - {selectedDelega?.ordine?.sigla_provincia}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Delegato C3i</label>
              <Select
                value={selectedDelega?.delegato_id || 'none'}
                onValueChange={(value) => setSelectedDelega({
                  ...selectedDelega,
                  delegato_id: value === 'none' ? null : value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona delegato..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {users
                    .filter(u => u.ordine_appartenenza_id === selectedDelega?.ordine_id)
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.nome} {u.cognome}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Delegato Supplente</label>
              <Select
                value={selectedDelega?.supplente_id || 'none'}
                onValueChange={(value) => setSelectedDelega({
                  ...selectedDelega,
                  supplente_id: value === 'none' ? null : value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona supplente..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {users
                    .filter(u => u.ordine_appartenenza_id === selectedDelega?.ordine_id)
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.nome} {u.cognome}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>Annulla</Button>
            <Button onClick={handleSaveDelega}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}